/**
 * TermTooltip + GLOSSARY — Non-HR UX layer (HUMANAI_UX_NONHR.md §3-4, หลักการ 1).
 *
 * "ภาษาคน ไม่ใช่ภาษา HR". ศัพท์กฎหมาย/บัญชีที่เลี่ยงไม่ได้ → ห่อด้วย <Term>
 * ให้ผู้ใช้แตะดูคำอธิบายภาษาคน + อ้างอิงมาตรา. ผู้ใช้ที่ไม่ใช่ HR ไม่ต้อง
 * เปิดคู่มือแยก — ความช่วยเหลืออยู่ที่จุดงาน.
 *
 * GLOSSARY = seed (ดู §4). ปลายทางย้ายไป DB ให้ปุ้ยเติม/แก้เองไม่ต้อง deploy
 * (HUMANAI_DIRECTION §7) — โครง component ไม่ต้องเปลี่ยน แค่สลับแหล่งข้อมูล.
 *
 * Mobile-first: แตะเปิด popover (ไม่พึ่ง hover). Portable ข้าม product.
 */
import { useState } from 'react';
import { HelpCircle } from 'lucide-react';

export interface GlossaryEntry {
  /** label ภาษาคนที่จะโชว์ (ถ้าไม่ override ด้วย children) */
  label: string;
  /** คำอธิบายภาษาคน */
  explain: string;
  /** อ้างอิงกฎหมาย/ที่มา (เลือกใส่) */
  cite?: string;
}

/** Seed glossary — ตรงกับ §4 ของ HUMANAI_UX_NONHR.md */
export const GLOSSARY: Record<string, GlossaryEntry> = {
  severance: {
    label: 'ค่าชดเชย',
    explain: 'เงินที่กฎหมายบังคับให้จ่ายเมื่อเลิกจ้างโดยลูกจ้างไม่ได้ทำผิด คิดตามอายุงาน',
    cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.118',
  },
  notice_pay: {
    label: 'ค่าบอกกล่าวล่วงหน้า',
    explain: 'ถ้าให้ออกทันทีโดยไม่บอกล่วงหน้าอย่างน้อย 1 งวดจ่ายค่าจ้าง ต้องจ่ายเงินก้อนนี้แทน',
    cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.17',
  },
  accrued_leave: {
    label: 'วันลาสะสม',
    explain: 'วันลาพักร้อนที่ยังไม่ได้ใช้ ต้องจ่ายเป็นเงินคืนเมื่อออกจากงาน',
  },
  sso: {
    label: 'ประกันสังคม',
    explain: 'เงินสมทบที่หักจากเงินเดือน 5% (นายจ้างสมทบเท่ากัน) ส่งสำนักงานประกันสังคม',
  },
};

/**
 * <Term name="severance" /> — โชว์ label + แตะดูคำอธิบาย.
 * ใส่ children เพื่อ override ข้อความที่โชว์ (คำอธิบายยังมาจาก glossary).
 */
export function Term({ name, children }: { name: string; children?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const entry = GLOSSARY[name];
  if (!entry) return <>{children ?? name}</>;

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-0.5 text-inherit underline decoration-dotted decoration-gray-400 underline-offset-2"
        aria-label={`คำอธิบาย: ${entry.label}`}
      >
        {children ?? entry.label}
        <HelpCircle className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <>
          {/* backdrop กดปิด */}
          <button type="button" className="fixed inset-0 z-40 cursor-default" aria-hidden onClick={() => setOpen(false)} />
          <span className="absolute left-0 top-full mt-1 z-50 w-64 card p-3 text-left shadow-md">
            <span className="block text-sm font-medium text-gray-900">{entry.label}</span>
            <span className="block text-sm text-gray-600 mt-0.5">{entry.explain}</span>
            {entry.cite && <span className="block text-xs text-gray-400 mt-1">อ้างอิง: {entry.cite}</span>}
          </span>
        </>
      )}
    </span>
  );
}
